import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CheckIcon, CopyIcon } from './icons'

/**
 * Panoya kopyalama - `execCommand` yedegiyle.
 *
 * `navigator.clipboard` SADECE guvenli baglamda (`https` veya `localhost`)
 * var. Bu uygulama LAN'da `http://hedefai.local:8080` uzerinden de servis
 * ediliyor ve orada `navigator.clipboard` TANIMSIZ - kontrol etmeden
 * cagirmak `TypeError` firlatir ve buton sessizce olur.
 *
 * Bu yuzden vanilla arayuzdeki `execCommand` yedegi AYNEN korunuyor.
 * `execCommand` deprecated ama calisiyor ve alternatifi yok.
 */
export function CopyButton({
  text,
  label,
  iconOnly = false,
}: {
  text: string
  label?: string
  /** Metin YOK, sadece ikon (kopyalanınca `CheckIcon`a doner). Etiket erisilebilirlik icin aria-label'a tasinir. */
  iconOnly?: boolean
}) {
  const { t } = useTranslation()
  // Varsayilan etiket RENDER aninda cevriliyor. Parametre
  // varsayilani olarak yazmak (`label = t(...)`) modul seviyesinde
  // degerlendirilemez ve dil degistiginde guncellenmezdi.
  const shown = label ?? t('common.copy')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])

  const copy = useCallback(async () => {
    if (await writeClipboard(text)) setCopied(true)
  }, [text])

  if (iconOnly) {
    return (
      <button
        type="button"
        className="copy copy-icon-only"
        onClick={copy}
        aria-label={copied ? t('common.copied') : shown}
        aria-live="polite"
      >
        {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
      </button>
    )
  }

  return (
    <button type="button" className="copy" onClick={copy} aria-live="polite">
      {copied ? t('common.copied') : shown}
    </button>
  )
}

export async function writeClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Izin reddedildi veya guvenli olmayan baglam: yedege dusuyoruz.
    }
  }
  return legacyCopy(text)
}

function legacyCopy(text: string): boolean {
  const area = document.createElement('textarea')
  area.value = text
  // Ekran disina degil, gorunmez: `position: fixed` + `opacity: 0` odak
  // kaymasini ve iOS'ta sayfa zipplamasini engelliyor.
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.top = '0'
  area.style.opacity = '0'
  document.body.appendChild(area)
  area.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(area)
  return ok
}
